import React from 'react';

/**
 * Admin-wide deep links: /admin?group=<group key>&tab=<sub-tab>&<params>.
 *
 * One URL shape for "open this exact thing in the admin", so a panel can link
 * into another group without knowing how that group is built:
 *   /admin?group=horizon&tab=technologies&tech=UTECH-AGI-000006
 *   /admin?group=tech_pages&tab=tech&target=UTECH-AGI-000006
 *
 * AdminPage owns the URL: it reads the query on load and on every in-app
 * navigation (Back/Forward included), switches to the group and hands the
 * parsed target to that group's panel, which applies whatever it understands
 * (a sub-tab, a filter, a drawer to open) and ignores the rest.
 *
 * Inside the admin a link is an in-app navigation (openAdmin → the event
 * below → AdminPage pushes the URL); Cmd/Ctrl-click still opens a new tab,
 * because the href is the real deep link.
 */

export const ADMIN_NAV_EVENT = 'utech:admin-nav';

export type AdminTarget = { group: string; tab?: string } & Record<string, string | undefined>;
/** What a panel receives: the target plus a nonce that changes on every navigation. */
export type AdminTargetIn = AdminTarget & { nonce: string };

export const adminHref = (t: AdminTarget): string => {
  const p = new URLSearchParams();
  p.set('group', t.group);
  Object.entries(t).forEach(([k, v]) => {
    if (k !== 'group' && k !== 'nonce' && v !== undefined && v !== null && v !== '') p.set(k, String(v));
  });
  return `/admin?${p.toString()}`;
};

/** The deep link in a query string, or null when it names no group. */
export const parseAdminTarget = (search: string): AdminTarget | null => {
  const p = new URLSearchParams(search || '');
  const group = (p.get('group') || '').trim();
  if (!group) return null;
  const out: AdminTarget = { group };
  p.forEach((v, k) => { if (k !== 'group' && v !== '') out[k] = v; });
  return out;
};

/** In-app navigation to a deep link. Outside the admin shell (nothing listening)
 *  it falls back to a normal page load of the same URL. */
export const openAdmin = (t: AdminTarget) => {
  const ev = new CustomEvent(ADMIN_NAV_EVENT, { detail: t, cancelable: true });
  const handled = !document.dispatchEvent(ev);
  if (!handled) window.location.assign(adminHref(t));
};

export const AdminLink: React.FC<{
  to: AdminTarget; children: React.ReactNode; className?: string; title?: string;
}> = ({ to, children, className, title }) => (
  <a href={adminHref(to)} title={title}
    onClick={e => {
      // Let the browser handle new-tab / new-window clicks on the real URL.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      openAdmin(to);
    }}
    className={className || 'text-primary-700 underline underline-offset-2 whitespace-nowrap'}>
    {children}
  </a>
);
